import { Box, Card, Grid, Group, Text } from "@mantine/core"
import { formatDate, formatDateTime } from "@medplum/core"
import {
    Appointment,
    Consent,
    Patient,
    Group as PatientGroup,
    Practitioner,
    ResourceType,
    Task,
} from "@medplum/fhirtypes"
import { useMedplum, useMedplumNavigate } from "@medplum/react"
import React, { useCallback, useEffect, useState } from "react"
import { useReadyToFetch } from "../../hooks/useCheckReadyToFetch"
import { ICards, ITableColumn } from "../../types/interfaces"
import { getName, getPatientFromAppointment } from "../../utils/tables"
import TableWithControlsDashBoard from "../tables/TableWithControlsDashBoard"
import FeaturedCards from "./FeaturedCards"
import practitionerImage from "../../assets/images/dashboard-practitioners.svg"
import patientImage from "../../assets/images/dashboard-patients.svg"
import consentImage from "../../assets/images/dashboard-consents.svg"
import appointmentImage from "../../assets/images/dashboard-appointments.svg"
export function Dashboard(): JSX.Element {
    const medplum = useMedplum()
    const readyToFetch = useReadyToFetch()
    const [patients, setPatients] = useState<Patient[]>([])
    const [tasks, setTasks] = useState<Task[]>([])
    const [practitioners, setPractitioners] = useState<Practitioner[]>([])
    const [groups, setGroups] = useState<PatientGroup[]>([])
    const [consents, setConsents] = useState<Consent[]>([])
    const [consentCounts, setConsentCounts] = useState<{
        total: number
        active: number
        pending: number
    }>({ total: 0, active: 0, pending: 0 })
    const [appointments, setAppointments] = useState<Appointment[]>([])
    const [cardiology, setCardiologist] = useState<number>(0)
    const [dermatology, setDermatology] = useState<number>(0)

    // Add state for monthly counts
    const [currentMonthCounts, setCurrentMonthCounts] = useState<
        Record<string, number>
    >({
        patient: 0,
        practitioner: 0,
        consent: 0,
        appointment: 0,
    })
    const [lastMonthCounts, setLastMonthCounts] = useState<
        Record<string, number>
    >({
        patient: 0,
        practitioner: 0,
        consent: 0,
        appointment: 0,
    })

    const navigate = useMedplumNavigate()

    const getMonthRange = (isCurrentMonth: boolean) => {
        const now = new Date()
        const start = new Date(
            now.getFullYear(),
            now.getMonth() + (isCurrentMonth ? 0 : -1),
            1
        )
        const end = new Date(
            now.getFullYear(),
            now.getMonth() + (isCurrentMonth ? 1 : 0),
            0
        )

        // Format dates in FHIR format (YYYY-MM-DD)
        const formatDate = (date: Date) => {
            return date.toISOString().split("T")[0]
        }

        return {
            start: formatDate(start),
            end: formatDate(end),
        }
    }

    const calculateComparisonPercentage = (
        current: number,
        last: number
    ): number => {
        if (last === 0) {
            return current === 0 ? 0 : 100 // If last month was 0, and current has data, show 100% increase
        }
        if (current === 0) {
            return -100 // If current month is 0, show 100% decrease
        }
        const percentage = ((current - last) / last) * 100

        return Math.round(percentage)
    }

    const fetchMonthlyCounts = useCallback(
        async (resourceType: ResourceType) => {
            if (!readyToFetch) return

            const currentMonth = getMonthRange(true)
            const lastMonth = getMonthRange(false)

            try {
                // Fetch current month count
                const currentMonthData = await medplum.search(resourceType, [
                    ["_summary", "count"],
                    ["_lastUpdated", `ge${currentMonth.start}`],
                    ["_lastUpdated", `le${currentMonth.end}`],
                ])

                // Fetch last month count
                const lastMonthData = await medplum.search(resourceType, [
                    ["_summary", "count"],
                    ["_lastUpdated", `ge${lastMonth.start}`],
                    ["_lastUpdated", `le${lastMonth.end}`],
                ])

                setCurrentMonthCounts((prev) => ({
                    ...prev,
                    [resourceType.toLowerCase()]: currentMonthData.total || 0,
                }))

                setLastMonthCounts((prev) => ({
                    ...prev,
                    [resourceType.toLowerCase()]: lastMonthData.total || 0,
                }))
            } catch (err) {
                console.error(
                    `Error while fetching ${resourceType} monthly counts:`,
                    err
                )
            }
        },
        [medplum, readyToFetch]
    )

    const fetchData = useCallback(
        (resourceType: ResourceType) => {
            if (!readyToFetch) return
            switch (resourceType) {
                case "Patient":
                    medplum
                        .searchResources("Patient", { _count: 300 })
                        .then((data) => setPatients(data))
                        .catch((err) =>
                            console.error(
                                "Error while fetching data of all patients: ",
                                err
                            )
                        )
                    fetchMonthlyCounts("Patient")
                    break

                case "Task":
                    medplum
                        .searchResources("Task", { _count: 10 })
                        .then((data) => setTasks(data))
                        .catch((err) =>
                            console.error(
                                "Error while fetching data of all patients: ",
                                err
                            )
                        )
                    break

                case "Practitioner":
                    medplum
                        .searchResources("Practitioner", {
                            _count: 100,
                        })
                        .then((data) => setPractitioners(data))
                        .catch((err) =>
                            console.error(
                                "Error while fetching data of all practitioners: ",
                                err
                            )
                        )
                    fetchMonthlyCounts("Practitioner")
                    break

                case "Appointment":
                    medplum
                        .searchResources("Appointment", {
                            _count: 10,
                        })
                        .then((data) => setAppointments(data))
                        .catch((err) =>
                            console.error(
                                "Error while fetching appointments: ",
                                err
                            )
                        )
                    fetchMonthlyCounts("Appointment")
                    break

                case "Consent":
                    // Get total consent count
                    medplum
                        .search("Consent", [["_summary", "count"]])
                        .then((data) => {
                            setConsentCounts((prev) => ({
                                ...prev,
                                total: data.total || 0,
                            }))
                        })
                        .catch((err) =>
                            console.error(
                                "Error while fetching total consents: ",
                                err
                            )
                        )

                    // Get active consent count
                    medplum
                        .search("Consent", [
                            ["_summary", "count"],
                            ["status", "active"],
                        ])
                        .then((data) => {
                            setConsentCounts((prev) => ({
                                ...prev,
                                active: data.total || 0,
                            }))
                        })
                        .catch((err) =>
                            console.error(
                                "Error while fetching active consents: ",
                                err
                            )
                        )

                    // Get pending consent count
                    medplum
                        .search("Consent", [
                            ["_summary", "count"],
                            ["status", "draft"],
                        ])
                        .then((data) => {
                            setConsentCounts((prev) => ({
                                ...prev,
                                pending: data.total || 0,
                            }))
                        })
                        .catch((err) =>
                            console.error(
                                "Error while fetching pending consents: ",
                                err
                            )
                        )

                    fetchMonthlyCounts("Consent")
                    break

                case "PractitionerRole":
                    medplum
                        .search("PractitionerRole", [
                            ["_summary", "count"],
                            ["specialty", "Cardiology"],
                        ])
                        .then((data) => {
                            setCardiologist(data.total || 0)
                        })
                        .catch((err) =>
                            console.error(
                                "Error while fetching cardiologists: ",
                                err
                            )
                        )

                    medplum
                        .search("PractitionerRole", [
                            ["_summary", "count"],
                            ["specialty", "General practice"],
                        ])
                        .then((data) => {
                            setDermatology(data.total || 0)
                        })
                        .catch((err) =>
                            console.error("Error while fetching GPs: ", err)
                        )
                    break
            }
        },
        [medplum, readyToFetch, fetchMonthlyCounts]
    )

    useEffect(() => {
        fetchData("Patient")
        fetchData("Practitioner")
        // fetchData("Group");
        fetchData("Consent")
        fetchData("Appointment")
        fetchData("Task")
        fetchData("PractitionerRole")
    }, [fetchData])

    // const [statisticCountsRes, setStatisticCountsRes] = useState<any | null>();
    // const [notificationList, setNotificationList] = useState();

    // const notificationsList = useCallback(() => {
    // 	getNotifications(medplum)
    // 		.then((response) => {
    // 			setNotificationList(response?.data.entry);
    // 		})
    // 		.catch((error) => {
    // 			console.error(error);
    // 		});
    // }, [medplum]);

    const cardsData: ICards[] = [
        {
            ImageSrc: practitionerImage,
            text: "Practitioners",
            subText: "Practitioners",
            counts: practitioners.length,
            routeTo: "/Practitioner",
            bg: "", //"rgba(128,0,128,0.1)",
            progressHighlight: "#9581D4",
            progressBg: "#E2DAFD",
            isCheckCard: true,
            comparisonPercentage: calculateComparisonPercentage(
                currentMonthCounts.practitioner,
                lastMonthCounts.practitioner
            ),
            featuredValues: [
                `${cardiology} Cardiologist`,
                `${dermatology} GP`,
                // practitioners.filter((c) => c.gender === "male").length + " Male",
                // practitioners.filter((c) => c.gender === "female").length + " Female",
            ],
        },
        {
            ImageSrc: patientImage,
            text: "Patients",
            subText: "Patients",
            counts: patients.length,
            routeTo: "/Patient",
            bg: "", //"rgba(0,0,255,0.1)",
            progressHighlight: "#82A4D4",
            progressBg: "#D7E6FB",
            isCheckCard: true,
            comparisonPercentage: calculateComparisonPercentage(
                currentMonthCounts.patient,
                lastMonthCounts.patient
            ),
            featuredValues: [
                patients.filter((c) => c.gender === "male").length + " Male",
                patients.filter((c) => c.gender === "female").length +
                    " Female",
                // patients.filter((c) => c.gender === "other").length + " Child",
            ],
        },
        {
            ImageSrc: consentImage,
            text: "Consents",
            subText: "Consents",
            counts: consentCounts.total,
            routeTo: "/Consent",
            bg: "", //"rgba(0,128,0,0.1)",
            progressHighlight: "#8AD8C7",
            progressBg: "#D3FAF2",
            isCheckCard: true,
            comparisonPercentage: calculateComparisonPercentage(
                currentMonthCounts.consent,
                lastMonthCounts.consent
            ),
            featuredValues: [
                `${consentCounts.active} Approved`,
                `${consentCounts.pending} Pending`,
            ],
        },
        {
            ImageSrc: appointmentImage,
            text: "Appointments",
            subText: "Appointments",
            counts: appointments.length,
            routeTo: "/",
            bg: "", //"rgba(0,128,0,0.1)",
            progressHighlight: "#8AD8C7",
            progressBg: "#D3FAF2",
            isCheckCard: true,
            comparisonPercentage: calculateComparisonPercentage(
                currentMonthCounts.appointment,
                lastMonthCounts.appointment
            ),
            featuredValues: [
                appointments.filter(
                    (a) =>
                        a.start &&
                        new Date(a.start).toDateString() ===
                            new Date().toDateString()
                ).length + " today",
                appointments.filter(
                    (a) =>
                        a.start &&
                        new Date(a.start) > new Date() &&
                        new Date(a.start) <=
                            new Date(
                                new Date().setDate(new Date().getDate() + 7)
                            )
                ).length + " next week",
            ],
        },
        // {
        // 	ImageSrc: "/assets/images/dashboard-assets/square-plus.svg",
        // 	text: "Clinical Trials",
        // 	subText: "Clinical Trials",
        // 	counts: researchStudies.length,
        // 	routeTo: "/Trials",
        // 	bg: "rgba(255,255,0,0.1)",
        // 	progressHighlight: "#E1CEA8",
        // 	progressBg: "#F4E9D3",
        // 	isCheckCard: true,
        // },
        // {
        // 	ImageSrc: "/assets/images/dashboard-assets/procedures.svg",
        // 	text: "Procedures",
        // 	subText: "Procedures",
        // 	counts: procedures.length,
        // 	routeTo: "#",
        // 	bg: "rgba(128,128,128,0.1)",
        // 	progressHighlight: "#ADADAD",
        // 	progressBg: "#DBDBDB",
        // 	isCheckCard: false,
        // },
    ]

    const taskTableData: ITableColumn[] = [
        {
            columnName: "Patient",
            columnValues: tasks.map((task) => {
                const patient: Patient | undefined = patients.find(
                    (p) => task.for?.reference?.split("/")[1] === p.id!
                )
                const fullName: string = patient ? getName(patient) : "-"
                const lastName: string = patient?.name?.[0]?.family || "-"
                return {
                    id: task.id!,
                    value: fullName,
                    sortValue: lastName,
                    searchValue: fullName,
                }
            }),
            sortable: true,
        },
        {
            columnName: "Description",
            columnValues: tasks.map((task) => {
                const description: string = task?.description || "-"
                return {
                    id: task.id!,
                    value: description,
                }
            }),
            sortable: false,
        },
        {
            columnName: "Created At",
            columnValues: tasks.map((task) => {
                const date: string = task?.authoredOn || "-"
                return {
                    id: task.id!,
                    value: formatDate(date),
                    sortValue: date,
                }
            }),
            sortable: true,
        },
        {
            columnName: "Created By",
            columnValues: tasks.map((task) => {
                const practitioner: Practitioner | undefined =
                    practitioners.find(
                        (pr) => task.owner?.reference?.split("/")[1] === pr.id!
                    )
                const fullName: string = practitioner
                    ? getName(practitioner)
                    : "-"
                const lastName: string = practitioner?.name?.[0]?.family || "-"
                return {
                    id: task.id!,
                    value: fullName,
                    sortValue: lastName,
                    searchValue: fullName,
                }
            }),
            sortable: true,
        },
    ]

    const appointmentTableData: ITableColumn[] = [
        {
            columnName: "Patient Name",
            columnValues: appointments.map((a) => ({
                id: a.id!,
                value: (
                    <Text fw={500}>
                        {getName(
                            getPatientFromAppointment(a, patients) ||
                                ({} as Patient)
                        )}
                    </Text>
                ),
                sortValue: getName(
                    getPatientFromAppointment(a, patients) || ({} as Patient)
                ),
            })),
            sortable: true,
        },
        {
            columnName: "Date",
            columnValues: appointments.map((a) => ({
                id: a.id!,
                value: formatDateTime(a.start, undefined, {
                    dateStyle: "short",
                    timeStyle: "short",
                }),
                sortValue: a.start,
            })),
            sortable: true,
        },
    ]

    return (
        <Card mt={10} mx={10}>
            <Box py={20}>
                <Grid>
                    <FeaturedCards cardsData={cardsData} />
                </Grid>
            </Box>
            <Group align="flex-start" sx={{ width: "100%" }}>
                <Box>
                    <TableWithControlsDashBoard
                        tableData={appointmentTableData}
                        onRowClick={() => {
                            // navigate("/Calendar");
                        }}
                        titleLabel="Upcoming Appointments"
                        resourceType="Appointment"
                        title="Appointments"
                        withCheckboxes={false}
                        showTop={false}
                    />
                </Box>
                <Box sx={{ width: "61%" }}>
                    <TableWithControlsDashBoard
                        tableData={taskTableData}
                        onRowClick={() => {
                            navigate("/Task")
                        }}
                        titleLabel="Tasks"
                        resourceType="Task"
                        title="Task"
                        withCheckboxes={false}
                        showTop={false}
                    />
                </Box>
            </Group>
            <Box px="lg" py="sm">
                <Grid mb="xl">
                    {/* <Grid.Col span={{ base: 12, lg: 8, sm: 12 }}>
            <OrganisationCard />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 4, sm: 12 }}>
            <NotificationsCard n
          </Grid.Col> */}
                </Grid>
            </Box>
        </Card>
    )
}
